import { Plus, XIcon } from "lucide-react";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { useCallback, useState } from "react";
import VendorsListAdminPartial from "./vendor-list.admin.partial";
import { useSearchVendorsHttp } from "../../../hooks/http/admin/vendors/search-vendors.admin.http";
import { debounce, handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import type { IVendor } from "../../../types/models/vendor.modals.types";

export default function VendorAdminPartial() {
  const [search, setSearch] = useState("");
  const [vendors, setVendors] = useState<IVendor[]>();
  const { toast } = useToast();
  const [typing, setTyping] = useState(false);
  const {
    execute: searchVendorsHttp,
    fetching: searchingVendors,
    cancel: cancelSearchVendors,
  } = useSearchVendorsHttp();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      cancelSearchVendors(); // Cancel previous request

      const response = await searchVendorsHttp({
        body: null,
        queryParams: { searchString: searchTerm },
      });

      if (!response.success) {
        return handleHttpError(response, toast);
      }

      setVendors(response.data.vendors);
      setTyping(false);
    }, 500),
    []
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTyping(true);
    setSearch(e.target.value);
    debouncedSearch(e.target.value);

    if (e.target.value.trim().length === 0) {
      setTyping(false);
      setVendors(undefined);
      cancelSearchVendors();
      document.getElementById("getVendors")?.click();
    }
  };

  return (
    <div className="flex flex-col justify-start items-center bg-background p-8 w-full min-h-screen text-primary container">
      <div className="flex justify-between items-center px-5 w-full">
        <p className="font-semibold text-2xl">Manage Vendors</p>
        <div className="flex items-center gap-4 py-4">
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={handleSearch}
              className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name, email, and number"
            />

            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  cancelSearchVendors();
                  setVendors(undefined);
                  setTyping(false);
                  document.getElementById("getVendors")?.click();
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
          <Link to="/admin/vendors/add-new">
            <Button className="flex gap-2 bg-primarybuttonbg px-10 py-7 font-semibold text-base text-black">
              <Plus className="w-4 h-4" /> Add vendors
            </Button>
          </Link>
        </div>
      </div>

      <VendorsListAdminPartial
        search={search}
        vendors={vendors}
        searching={searchingVendors || typing}
      />
    </div>
  );
}
